import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface User {
    id: string;
    full_name: string;
    email: string;
    phone?: string | null;
    role: {
        name: string;
        type?: string;
        permissions: { key: string }[];
    };
    account_status?: {
        id: string;
        status: string;
    } | null;
}

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isVerified: boolean;
    loading: boolean;
}

const getInitialState = (): AuthState => {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    return {
        user,
        isAuthenticated: !!storedUser,
        isVerified: user?.account_status?.id && user?.account_status?.status?.toLowerCase() !== 'pending' ? true : false,
        loading: false,
    };
};

const initialState: AuthState = getInitialState();

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (
            state,
            { payload: { user } }: PayloadAction<{ user: User }>
        ) => {
            state.user = user;
            state.isAuthenticated = true;
            state.isVerified = user.account_status?.id && user.account_status.status?.toLowerCase() !== "pending" ? true : false;
            localStorage.setItem('user', JSON.stringify(user));
        },
        setVerified: (state, action: PayloadAction<boolean>) => {
            state.isVerified = action.payload;
            if (state.user) {
                // Update user in state and localStorage
                const updatedUser = { ...state.user };
                if (action.payload && !updatedUser.account_status) {
                    // Set a placeholder account_status when verified
                    updatedUser.account_status = { id: 'verified', status: 'ACTIVE' };
                }
                state.user = updatedUser;
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
        },
        logout: (state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.isVerified = false;
            localStorage.removeItem('user');
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
    },
});

export const { setCredentials, logout, setLoading, setVerified } = authSlice.actions;
export default authSlice.reducer;
